import { useEffect, useMemo, useState } from "react";
import { MathJax } from "better-react-mathjax";
import { parseXML } from "./xmlParser";

export default function CommonTemplateRenderer({ type, url }) {
  const [xmlDoc, setXmlDoc] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);

  // Base path for images
  const basePath = useMemo(() => {
    if (!url) return "";
    return url.substring(0, url.lastIndexOf("/") + 1);
  }, [url]);

  //  Fetch data
useEffect(() => {
  if (!url) return;

  fetch(url)
    .then((res) => res.text())
    .then((xmlText) => {
      const xml = new DOMParser().parseFromString(xmlText, "text/xml");
      setXmlDoc(xml);
    })
    .catch(console.error);
}, [url]);

  //  Parse XML → JSON (Universal)
  const parsedData = useMemo(() => {
    if (!xmlDoc) return null;
    return parseXML(xmlDoc, type, basePath);
  }, [xmlDoc, type, basePath]);

  //  MathJax render
  useEffect(() => {
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise();
    }
  }, [parsedData, jsonData, showAnswers]);

  if (!xmlDoc && !jsonData) {
    return <div className="p-4 sm:p-10 text-white text-center">Loading...</div>;
  }

  
  //  WORKSHEET 
  
 if (type === "wks" && parsedData) {
  return (
    <div className="p-4 sm:p-8 text-white max-w-6xl mx-auto space-y-8 sm:space-y-10 w-full">
      <h1 className="text-2xl sm:text-4xl font-bold">Worksheet</h1>

      <button
        onClick={() => setShowAnswers(!showAnswers)}
        className="px-5 py-3 bg-indigo-600 rounded-xl"
      >
        {showAnswers ? "Hide" : "Show"}
      </button>

      {/* Label Diagram */}
      {parsedData.labelDiagrams.map((item, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl mb-4">Label Diagram</h2>
          <img
            src={`${basePath}${item.image}`}
            className="rounded-lg mb-4 w-full max-w-md"
            alt="Diagram"
          />
          <div dangerouslySetInnerHTML={{ __html: item.description }} />
        </div>
      ))}

      {/* Fill Blanks */}
      {parsedData.fillBlanks.length > 0 && (
        <div className="bg-white/5 rounded-xl p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl mb-4">Fill in the Blanks</h2>
          {parsedData.fillBlanks.map((q, i) => (
            <div key={i} className="mb-3">
              <div dangerouslySetInnerHTML={{ __html: q.question }} />
              {showAnswers && (
                <div className="text-green-400 mt-1">Ans: {q.answer}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* True False */}
      {parsedData.trueFalse.length > 0 && (
        <div className="bg-white/5 rounded-xl p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl mb-4">True / False</h2>
          {parsedData.trueFalse.map((q, i) => (
            <div key={i} className="mb-3">
              <div dangerouslySetInnerHTML={{ __html: q.question }} />
              {showAnswers && (
                <div className="text-green-400 mt-1">Ans: {q.answer}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* MCQ */}
      {parsedData.mcq.length > 0 && (
        <div className="bg-white/5 rounded-xl p-4 sm:p-6">
          <h2 className=" kruti text-xl sm:text-2xl mb-4">MCQ</h2>
          {parsedData.mcq.map((q, i) => (
            <div key={i}>
              {q.image && (
                <img
                  src={`${basePath}${q.image}`}
                className="kruti rounded-lg mb-4 w-full max-w-md"
                  alt="MCQ"
                />
              )}

              <div dangerouslySetInnerHTML={{ __html: q.question }} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {q.options.map((opt, idx) => (
                  <button
                    key={idx}
                    className="p-3 bg-white/10 rounded-lg text-left"
                  >
                    {String.fromCharCode(65 + idx)}.{" "}
                    <span dangerouslySetInnerHTML={{ __html: opt }} />
                  </button>
                ))}
              </div>

              {showAnswers && (
                <div className="text-green-400 mt-4">
                  Correct: Option {q.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

  if (!parsedData) {
    return <div className="p-10 text-white">No Data Found</div>;
  }

  //  CM (Graphic Organizer)
  if (type === "cm") {
    return (
    <div className="p-4 sm:p-8 text-white text-center">
        <h1 className="text-2xl sm:text-3xl mb-6">{parsedData.title}</h1>

        {parsedData.image && (
          <img
            src={
              parsedData.image.startsWith("http")
                ? parsedData.image
                : `${basePath}${parsedData.image}`
            }
            className="mx-auto rounded-xl shadow-xl max-w-full w-full"
            alt="Graphic Organizer"
          />
        )}
      </div>
    );
  }

  //  TS (Topic Synopsis)
 if (type === "ts") {
  return (
    <div className="p-4 sm:p-8 text-white max-w-6xl mx-auto space-y-6 w-full">
      <h1 className="text-2xl sm:text-3xl mb-6">{parsedData.title}</h1>

      {parsedData.content?.map((item, i) => {
        const tag = item.tag?.toLowerCase()?.trim();
        const value = item.value?.trim();

        return (
          <div key={i} className="template-card">
            <h2 className="text-xl mb-3 text-indigo-300">
              {item.label}
            </h2>

            {tag === "image" ? (
              <img
                src={`${basePath}${value}`}
                alt={item.label}
                className="rounded-xl max-w-full"
                onError={(e) =>
                  console.log("TS image failed:", e.target.src)
                }
              />
            ) : (
              <MathJax dynamic>
                <div
                  className="overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              </MathJax>
            )}
          </div>
        );
      })}
    </div>
  );
}

  //  RAP (Real Life Application)
  if (type === "rap") {
    return (
      <div className="p-8 text-white max-w-6xl mx-auto">
        <h1 className="text-3xl mb-6">{parsedData.title}</h1>

        {/* HEADER TEXT */}
        {parsedData.header && (
          <div
            className="mb-6"
            dangerouslySetInnerHTML={{ __html: parsedData.header }}
          />
        )}

        {parsedData.examples.map((ex, i) => (
          <div key={i} className="mb-8">
            <h2 className="text-xl mb-3 text-indigo-300">
              Example {i + 1}
            </h2>

            <MathJax dynamic>
              <div
                dangerouslySetInnerHTML={{ __html: ex.text }}
              />
            </MathJax>

            {ex.image && (
              <img
                src={`${basePath}${ex.image}`}
                className="mt-4 rounded-xl"
              />
            )}
          </div>
        ))}
      </div>
    );
  }

  //  DEFAULT (TI, AFL)

  return (
    <div className="p-8 text-white max-w-6xl mx-auto space-y-6">
      <h1 className="text-3xl mb-6">{parsedData.title}</h1>

      {parsedData.content?.map((item, i) => (
        <div key={i} className="template-card">
          <h2 className="text-lg sm:text-xl mb-3 text-indigo-300">
            {item.tag}
          </h2>

          <MathJax dynamic>
            <div
              className="overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: item.value }}
            />
          </MathJax>
        </div>
      ))}
    </div>
  );
}

